import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Cabecalho, Layout } from '../componentes/Layout.tsx';
import { Botao } from '../componentes/forma.tsx';
import { Lista } from '../componentes/painel.tsx';
import { api, ErroApi, query } from '../lib/api.ts';
import { dataCurta, diasAtras, hojeIso } from '../lib/formato.ts';
import './ocupacional.css';

/* Portal da clínica de medicina ocupacional.
 *
 * Ela vem aqui fazer uma coisa só: levar os laudos prontos para o sistema
 * dela. A tela mostra o mínimo para isso -- quem, quando, qual exame -- e nada
 * do que acontece por dentro da clínica de radiologia.
 *
 * Os dois estados respondem perguntas diferentes, e é por isso que são dois:
 *   "baixei?"   o sistema sabe, e marca sozinho quando o PDF sai
 *   "lancei?"   só ela sabe, e confirma marcando */

interface ExameOcupacional {
  id: string;
  codigo: number;
  dataExame: string;
  pacienteNome: string;
  pacienteMatricula: string | null;
  procedimentoNome: string;
  /** PORTAL: o laudo sai por um portal de fora, não por esta clínica. */
  modoEnvio: string;
  empresaNome: string | null;
  baixadoEm: string | null;
  arquivadoEm: string | null;
}

interface Resposta {
  itens: ExameOcupacional[];
  total: number;
  resumo: { pendentes: number; arquivados: number; nuncaBaixados: number };
}

const ABAS = [
  { chave: 'PENDENTES', rotulo: 'A lançar' },
  { chave: 'ARQUIVADOS', rotulo: 'Já lançados' },
  { chave: 'TODOS', rotulo: 'Todos' },
] as const;

/* O período começa em 30 dias, e não em "tudo".
   São mais de mil laudos ocupacionais no histórico migrado; abrir a tela com
   todos eles transforma o que é uma tarefa da semana numa lista impossível de
   ler. Quem precisa de um exame antigo troca o período ou busca pelo nome --
   e a tela avisa quando há algo pendente fora do que está sendo mostrado, para
   o filtro não esconder trabalho em silêncio. */
const PERIODOS = [
  { chave: 'HOJE', rotulo: 'Hoje', dias: 0 },
  { chave: '7', rotulo: '7 dias', dias: 7 },
  { chave: '30', rotulo: '30 dias', dias: 30 },
  { chave: 'TUDO', rotulo: 'Tudo', dias: null },
  { chave: 'PERIODO', rotulo: 'Escolher', dias: null },
] as const;

export function Ocupacional() {
  const qc = useQueryClient();
  const [estado, setEstado] = useState<(typeof ABAS)[number]['chave']>('PENDENTES');
  const [busca, setBusca] = useState('');
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [periodo, setPeriodo] = useState<(typeof PERIODOS)[number]['chave']>('30');
  const [de, setDe] = useState(() => diasAtras(30));
  const [ate, setAte] = useState(() => hojeIso());

  function escolherPeriodo(p: (typeof PERIODOS)[number]) {
    setPeriodo(p.chave);
    setMarcados(new Set());
    if (p.chave === 'TUDO' || p.chave === 'PERIODO') return;
    setDe(p.dias === 0 ? hojeIso() : diasAtras(p.dias!));
    setAte(hojeIso());
  }

  const semData = periodo === 'TUDO';
  const consulta = query({
    estado,
    busca: busca.trim() || undefined,
    de: semData ? undefined : de,
    ate: semData ? undefined : ate,
  });

  /* Quantos ficaram de fora do período. Sem este número, um exame pendente de
     dois meses atrás simplesmente não existiria para quem abre a tela. */
  const consultaTudo = query({ estado: 'PENDENTES' });
  const pendentesNoTotal = useQuery({
    queryKey: ['ocupacional', 'pendentes-total', consultaTudo],
    queryFn: ({ signal }) => api.get<Resposta>(`/ocupacional/exames${consultaTudo}`, signal),
    staleTime: 60_000,
  });

  const lista = useQuery({
    queryKey: ['ocupacional', consulta],
    queryFn: ({ signal }) => api.get<Resposta>(`/ocupacional/exames${consulta}`, signal),
    placeholderData: (anterior) => anterior,
  });

  const arquivar = useMutation({
    mutationFn: (v: { ids: string[]; arquivado: boolean }) => api.post('/ocupacional/arquivar', v),
    onSuccess: () => {
      setMarcados(new Set());
      void qc.invalidateQueries({ queryKey: ['ocupacional'] });
    },
    onError: (e) => alert(e instanceof ErroApi ? e.message : 'Não foi possível salvar.'),
  });

  const itens = lista.data?.itens ?? [];
  const resumo = lista.data?.resumo;

  const alternar = (id: string) =>
    setMarcados((s) => {
      const novo = new Set(s);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });

  const todosMarcados = itens.length > 0 && itens.every((e) => marcados.has(e.id));

  return (
    <Layout>
      <Cabecalho titulo="Laudos para retirar" contagem={`${resumo?.pendentes ?? ''}`} />

      <div className="pp">
        <div className="pp__topo cartao">
          <nav className="abas" aria-label="O que mostrar">
            {ABAS.map((a) => (
              <button
                key={a.chave}
                className="aba"
                data-ativa={estado === a.chave || undefined}
                onClick={() => {
                  setEstado(a.chave);
                  setMarcados(new Set());
                }}
              >
                {a.rotulo}
                {a.chave === 'PENDENTES' && resumo ? (
                  <span className="aba__contagem">{resumo.pendentes}</span>
                ) : null}
              </button>
            ))}
          </nav>

          <input
            className="entrada pp__busca"
            placeholder="Nome do funcionário ou número do exame"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />

          <div className="pp__periodo">
            <span className="rotulo">Período</span>
            <div className="pp__periodo-abas" role="group" aria-label="Período dos exames">
              {PERIODOS.map((p) => (
                <button
                  key={p.chave}
                  type="button"
                  className="pp__periodo-aba"
                  data-ativa={periodo === p.chave || undefined}
                  onClick={() => escolherPeriodo(p)}
                >
                  {p.rotulo}
                </button>
              ))}
            </div>

            {periodo === 'PERIODO' && (
              <div className="pp__periodo-datas">
                <input
                  className="entrada"
                  type="date"
                  value={de}
                  max={ate}
                  aria-label="De"
                  onChange={(e) => setDe(e.target.value)}
                />
                <span>até</span>
                <input
                  className="entrada"
                  type="date"
                  value={ate}
                  min={de}
                  aria-label="Até"
                  onChange={(e) => setAte(e.target.value)}
                />
              </div>
            )}

            {!semData && periodo !== 'PERIODO' && (
              <span className="pp__periodo-intervalo">
                {dataCurta(de)} a {dataCurta(ate)}
              </span>
            )}
          </div>
        </div>

        {/* O filtro de data não pode esconder trabalho em silêncio: se há
            pendente fora do período mostrado, a tela diz quantos e leva até
            eles em um clique. */}
        {!semData
          && estado === 'PENDENTES'
          && (pendentesNoTotal.data?.total ?? 0) > (resumo?.pendentes ?? 0) && (
            <div className="pp__fora cartao">
              <span>
                {(pendentesNoTotal.data!.total - (resumo?.pendentes ?? 0))} exame(s) a lançar
                fora deste período.
              </span>
              <Botao
                variante="neutro"
                tamanho="sm"
                onClick={() => escolherPeriodo(PERIODOS.find((p) => p.chave === 'TUDO')!)}
              >
                Ver todos
              </Botao>
            </div>
          )}

        {/* A barra de seleção só aparece com algo marcado: sem isso ela ocuparia
            espaço permanente numa tela que precisa caber inteira. */}
        {marcados.size > 0 && (
          <div className="pp__barra cartao">
            <span>
              {marcados.size} {marcados.size === 1 ? 'exame marcado' : 'exames marcados'}
            </span>
            <Botao
              variante="primario"
              tamanho="sm"
              carregando={arquivar.isPending}
              onClick={() =>
                arquivar.mutate({ ids: [...marcados], arquivado: estado !== 'ARQUIVADOS' })
              }
            >
              {estado === 'ARQUIVADOS' ? 'Desmarcar' : 'Marcar como lançados'}
            </Botao>
            <Botao variante="silencioso" tamanho="sm" onClick={() => setMarcados(new Set())}>
              Limpar seleção
            </Botao>
          </div>
        )}

        <Lista
          carregando={lista.isPending}
          erro={lista.error instanceof ErroApi ? lista.error.message : undefined}
          recarregando={lista.isFetching && !lista.isPending}
          vazio={
            itens.length === 0
              ? estado === 'PENDENTES'
                ? { titulo: 'Nada pendente', apoio: 'Todos os laudos prontos já foram lançados.' }
                : { titulo: 'Nenhum exame aqui', apoio: 'Ajuste a busca ou troque de aba.' }
              : undefined
          }
        >
          <table className="tabela pp__tabela">
            <thead>
              <tr>
                <th className="pp__coluna-marca">
                  <input
                    type="checkbox"
                    aria-label="Marcar todos"
                    checked={todosMarcados}
                    onChange={() =>
                      setMarcados(todosMarcados ? new Set() : new Set(itens.map((e) => e.id)))
                    }
                  />
                </th>
                <th className="tabela--num">Data</th>
                <th>Funcionário</th>
                <th>Exame</th>
                <th className="pp__coluna-acao" />
              </tr>
            </thead>
            <tbody>
              {itens.map((e) => (
                <tr key={e.id} data-marcada={marcados.has(e.id) || undefined}>
                  <td className="pp__coluna-marca">
                    <input
                      type="checkbox"
                      aria-label={`Marcar exame ${e.codigo}`}
                      checked={marcados.has(e.id)}
                      onChange={() => alternar(e.id)}
                    />
                  </td>
                  <td className="tabela--num mono">{dataCurta(e.dataExame)}</td>
                  <td className="pp__funcionario">
                    {e.pacienteNome}
                    {e.empresaNome && <span className="pp__empresa">{e.empresaNome}</span>}
                  </td>
                  <td className="pp__exame" title={e.procedimentoNome}>
                    {e.procedimentoNome}
                    {/* Quem retira precisa saber, antes de abrir o arquivo, que
                        o laudo deste exame não está aqui: ele sai pelo portal.
                        Sem a marca, a pessoa baixa o documento e só descobre ao
                        ler que não há laudo nenhum na folha. */}
                    {e.modoEnvio === 'PORTAL' && <span className="pp__portal">Portal</span>}
                  </td>
                  <td className="pp__coluna-acao">
                    {/* O download é o registro: abrir o PDF já marca que o
                        arquivo saiu daqui. */}
                    <a
                      className={`botao botao--${e.baixadoEm ? 'neutro' : 'primario'} botao--sm`}
                      href={`/api/ocupacional/exames/${e.id}/pdf`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {e.baixadoEm ? 'Baixar de novo' : 'Baixar'}
                    </a>
                    {e.arquivadoEm ? (
                      <span className="pp__selo" data-estado="lancado">
                        lançado
                      </span>
                    ) : e.baixadoEm ? (
                      <span className="pp__selo" data-estado="baixado">
                        baixado
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Lista>

        <p className="campo__dica pp__rodape">
          O sistema marca sozinho quando você baixa um laudo. A caixa ao lado é para você
          confirmar que já lançou no sistema da clínica — é ela que decide o que continua
          aparecendo em <strong>A lançar</strong>.
        </p>
      </div>
    </Layout>
  );
}
